// ============================================================
// RFW — Bloque 9: Notificaciones Inteligentes Hub
// Unifica: 8 eventos, dashboard KPIs, configuración x4 entidades,
// plan limits. Todo lo que trae usuarios de regreso.
// ============================================================

// --- TIPOS DE EVENTO (8) ---

export interface NotificationEventType {
  id: string;
  label: string;
  description: string;
  icon: string;
  color: string;
  bgColor: string;
  category: "radio" | "programa" | "locutor" | "club" | "contenido";
}

export const notificationEventTypes: NotificationEventType[] = [
  {
    id: "radio_live_started",
    label: "Radio en Vivo",
    description: "Cuando una radio que seguís inicia transmisión",
    icon: "ri-broadcast-line",
    color: "text-rose-600",
    bgColor: "bg-rose-100",
    category: "radio",
  },
  {
    id: "program_started",
    label: "Programa Inició",
    description: "Cuando un programa que seguís comienza al aire",
    icon: "ri-calendar-check-line",
    color: "text-emerald-600",
    bgColor: "bg-emerald-100",
    category: "programa",
  },
  {
    id: "host_started",
    label: "Locutor Inició",
    description: "Cuando un locutor que seguís entra en cabina",
    icon: "ri-mic-line",
    color: "text-amber-600",
    bgColor: "bg-amber-100",
    category: "locutor",
  },
  {
    id: "new_contest",
    label: "Nuevo Concurso",
    description: "Cuando se lanza un concurso de tus radios o clubes",
    icon: "ri-trophy-line",
    color: "text-orange-600",
    bgColor: "bg-orange-100",
    category: "contenido",
  },
  {
    id: "new_event",
    label: "Nuevo Evento",
    description: "Cuando se publica un evento nuevo de tus entidades seguidas",
    icon: "ri-calendar-event-line",
    color: "text-violet-600",
    bgColor: "bg-violet-100",
    category: "contenido",
  },
  {
    id: "winner_announced",
    label: "Ganador Anunciado",
    description: "Cuando se anuncia el ganador de un concurso que seguías",
    icon: "ri-medal-line",
    color: "text-yellow-600",
    bgColor: "bg-yellow-100",
    category: "contenido",
  },
  {
    id: "new_sponsor",
    label: "Nuevo Patrocinador",
    description: "Cuando una radio suma un patrocinador nuevo con beneficios",
    icon: "ri-hand-heart-line",
    color: "text-cyan-600",
    bgColor: "bg-cyan-100",
    category: "contenido",
  },
  {
    id: "new_post",
    label: "Nueva Publicación",
    description: "Cuando se publica contenido nuevo de entidades que seguís",
    icon: "ri-chat-3-line",
    color: "text-sky-600",
    bgColor: "bg-sky-100",
    category: "contenido",
  },
];

// --- DASHBOARD KPIs ---

export interface NotificationDashboardKpi {
  id: string;
  label: string;
  value: number;
  displayValue: string;
  icon: string;
  trend: "up" | "down" | "stable";
  trendPercent: number;
  color: string;
}

export const notificationDashboardKPIs: NotificationDashboardKpi[] = [
  {
    id: "sent",
    label: "Notificaciones Enviadas",
    value: 1284932,
    displayValue: "1.28M",
    icon: "ri-send-plane-line",
    trend: "up",
    trendPercent: 12.4,
    color: "bg-primary-100 text-primary-700",
  },
  {
    id: "opened",
    label: "Notificaciones Abiertas",
    value: 487231,
    displayValue: "487K",
    icon: "ri-mail-open-line",
    trend: "up",
    trendPercent: 8.7,
    color: "bg-emerald-100 text-emerald-700",
  },
  {
    id: "ctr",
    label: "CTR Promedio",
    value: 37.9,
    displayValue: "37.9%",
    icon: "ri-mouse-line",
    trend: "up",
    trendPercent: 3.2,
    color: "bg-accent-100 text-accent-700",
  },
  {
    id: "conversion",
    label: "Tasa de Conversión",
    value: 18.3,
    displayValue: "18.3%",
    icon: "ri-user-follow-line",
    trend: "up",
    trendPercent: 5.1,
    color: "bg-amber-100 text-amber-700",
  },
];

// --- MÉTRICAS POR TIPO DE EVENTO ---

export interface EventTypeMetrics {
  eventId: string;
  sent: number;
  opened: number;
  ctr: number;
  conversion: number;
}

export const eventTypeMetrics: EventTypeMetrics[] = [
  { eventId: "radio_live_started", sent: 342100, opened: 142300, ctr: 41.6, conversion: 22.8 },
  { eventId: "program_started", sent: 289400, opened: 112500, ctr: 38.9, conversion: 19.2 },
  { eventId: "host_started", sent: 198200, opened: 78100, ctr: 39.4, conversion: 18.7 },
  { eventId: "new_contest", sent: 156800, opened: 62100, ctr: 39.6, conversion: 21.3 },
  { eventId: "new_event", sent: 124300, opened: 43100, ctr: 34.7, conversion: 14.8 },
  { eventId: "winner_announced", sent: 68700, opened: 24500, ctr: 35.7, conversion: 16.2 },
  { eventId: "new_sponsor", sent: 54200, opened: 14300, ctr: 26.4, conversion: 9.5 },
  { eventId: "new_post", sent: 51232, opened: 10331, ctr: 20.2, conversion: 7.8 },
];

// --- CONFIGURACIÓN POR RADIO ---

export interface RadioNotifConfig {
  radioId: number;
  radioName: string;
  radioImage: string;
  radioFrequency: string;
  events: { eventId: string; enabled: boolean }[];
}

export const radioNotifConfigs: RadioNotifConfig[] = [
  {
    radioId: 1, radioName: "La Mexicana FM", radioFrequency: "98.7 FM",
    radioImage: "https://readdy.ai/api/search-image?query=Mexican%20regional%20radio%20station%20logo%20with%20guitar%20and%20sombrero%20elements%2C%20circular%20design%20warm%20orange%20and%20green%20colors%2C%20clean%20modern%20broadcast%20style%2C%20festive%20vibrant%20feel%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=sn-notif-radio-1&orientation=squarish",
    events: [
      { eventId: "radio_live_started", enabled: true },
      { eventId: "program_started", enabled: true },
      { eventId: "new_contest", enabled: true },
      { eventId: "new_event", enabled: true },
      { eventId: "winner_announced", enabled: false },
      { eventId: "new_sponsor", enabled: true },
      { eventId: "new_post", enabled: true },
    ],
  },
  {
    radioId: 2, radioName: "Radio Nueva Actitud", radioFrequency: "102.5 FM",
    radioImage: "https://readdy.ai/api/search-image?query=Modern%20minimalist%20radio%20station%20logo%20design%20with%20musical%20notes%20and%20frequency%20waves%2C%20clean%20circular%20emblem%20style%2C%20warm%20golden%20and%20white%20color%20palette%2C%20simple%20geometric%20shapes%2C%20professional%20broadcast%20aesthetic%2C%20isolated%20on%20transparent%20background&width=200&height=200&seq=sn-notif-radio-2&orientation=squarish",
    events: [
      { eventId: "radio_live_started", enabled: true },
      { eventId: "program_started", enabled: false },
      { eventId: "new_contest", enabled: true },
      { eventId: "new_event", enabled: false },
      { eventId: "winner_announced", enabled: true },
      { eventId: "new_sponsor", enabled: false },
      { eventId: "new_post", enabled: true },
    ],
  },
  {
    radioId: 3, radioName: "Rock Total Radio", radioFrequency: "95.3 FM",
    radioImage: "https://readdy.ai/api/search-image?query=Rock%20radio%20station%20logo%20with%20electric%20guitar%20and%20lightning%20bolt%2C%20circular%20design%20black%20and%20red%20color%20scheme%2C%20modern%20rock%20broadcast%20emblem%2C%20energetic%20and%20bold%20style%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=sn-notif-radio-3&orientation=squarish",
    events: [
      { eventId: "radio_live_started", enabled: true },
      { eventId: "program_started", enabled: true },
      { eventId: "new_contest", enabled: false },
      { eventId: "new_event", enabled: true },
      { eventId: "winner_announced", enabled: true },
      { eventId: "new_sponsor", enabled: true },
      { eventId: "new_post", enabled: false },
    ],
  },
  {
    radioId: 4, radioName: "Banda Radio MX", radioFrequency: "107.9 FM",
    radioImage: "https://readdy.ai/api/search-image?query=Mexican%20banda%20music%20radio%20station%20logo%20with%20trumpet%20and%20accordion%2C%20circular%20design%20red%20white%20and%20green%20colors%2C%20festive%20broadcast%20emblem%2C%20traditional%20Mexican%20vibe%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=sn-notif-radio-4&orientation=squarish",
    events: [
      { eventId: "radio_live_started", enabled: true },
      { eventId: "program_started", enabled: true },
      { eventId: "new_contest", enabled: true },
      { eventId: "new_event", enabled: true },
      { eventId: "winner_announced", enabled: true },
      { eventId: "new_sponsor", enabled: false },
      { eventId: "new_post", enabled: true },
    ],
  },
  {
    radioId: 5, radioName: "La Kalle 99.9", radioFrequency: "99.9 FM",
    radioImage: "https://readdy.ai/api/search-image?query=Pop%20music%20radio%20station%20logo%20with%20headphones%20and%20music%20equalizer%20bars%2C%20circular%20design%20vibrant%20pink%20and%20coral%20colors%2C%20modern%20youthful%20broadcast%20emblem%2C%20energetic%20style%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=sn-notif-radio-5&orientation=squarish",
    events: [
      { eventId: "radio_live_started", enabled: true },
      { eventId: "program_started", enabled: false },
      { eventId: "new_contest", enabled: false },
      { eventId: "new_event", enabled: false },
      { eventId: "winner_announced", enabled: false },
      { eventId: "new_sponsor", enabled: true },
      { eventId: "new_post", enabled: true },
    ],
  },
  {
    radioId: 6, radioName: "Radio Fe y Esperanza", radioFrequency: "88.1 FM",
    radioImage: "https://readdy.ai/api/search-image?query=Christian%20radio%20station%20logo%20with%20dove%20symbol%2C%20circular%20minimalist%20design%2C%20soft%20warm%20colors%20white%20and%20gentle%20golden%20tones%2C%20clean%20modern%20broadcast%20emblem%2C%20spiritual%20community%20radio%2C%20isolated%20on%20transparent%20background%2C%20vector%20style%20flat%20design&width=200&height=200&seq=sn-notif-radio-6&orientation=squarish",
    events: [
      { eventId: "radio_live_started", enabled: false },
      { eventId: "program_started", enabled: true },
      { eventId: "new_contest", enabled: false },
      { eventId: "new_event", enabled: true },
      { eventId: "winner_announced", enabled: false },
      { eventId: "new_sponsor", enabled: false },
      { eventId: "new_post", enabled: true },
    ],
  },
];

// --- CONFIGURACIÓN POR PROGRAMA ---

export interface ProgramNotifConfig {
  programId: number;
  programName: string;
  programImage: string;
  radioName: string;
  events: { eventId: string; enabled: boolean }[];
}

export const programNotifConfigs: ProgramNotifConfig[] = [
  {
    programId: 1, programName: "El Sabor del Mediodía", radioName: "La Mexicana FM",
    programImage: "https://readdy.ai/api/search-image?query=Lively%20radio%20program%20cover%20art%20with%20warm%20Latin%20colors%2C%20food%20and%20music%20theme%2C%20bright%20festive%20design%2C%20square%20format%2C%20vibrant%20typography%2C%20clean%20modern%20aesthetic&width=200&height=200&seq=sn-notif-prog-1&orientation=squarish",
    events: [
      { eventId: "program_started", enabled: true },
      { eventId: "new_post", enabled: true },
      { eventId: "new_event", enabled: false },
      { eventId: "winner_announced", enabled: true },
    ],
  },
  {
    programId: 2, programName: "Tardes de Pop", radioName: "La Kalle 99.9",
    programImage: "https://readdy.ai/api/search-image?query=Colorful%20pop%20music%20radio%20show%20cover%20art%2C%20vibrant%20pink%20and%20coral%20tones%2C%20modern%20youthful%20design%20with%20music%20elements%2C%20square%20format%2C%20fun%20energetic%20vibe&width=200&height=200&seq=sn-notif-prog-2&orientation=squarish",
    events: [
      { eventId: "program_started", enabled: true },
      { eventId: "new_post", enabled: false },
      { eventId: "new_event", enabled: true },
      { eventId: "winner_announced", enabled: false },
    ],
  },
  {
    programId: 3, programName: "Rock Total en Vivo", radioName: "Rock Total Radio",
    programImage: "https://readdy.ai/api/search-image?query=Rock%20radio%20program%20cover%20art%20with%20guitars%20and%20concert%20lights%2C%20dark%20background%20with%20red%20and%20electric%20blue%20accents%2C%20square%20format%2C%20bold%20typography%2C%20concert%20photography%20style&width=200&height=200&seq=sn-notif-prog-3&orientation=squarish",
    events: [
      { eventId: "program_started", enabled: true },
      { eventId: "new_post", enabled: true },
      { eventId: "new_event", enabled: true },
      { eventId: "winner_announced", enabled: true },
    ],
  },
  {
    programId: 4, programName: "Mañanas de Bendición", radioName: "Radio Fe y Esperanza",
    programImage: "https://readdy.ai/api/search-image?query=Christian%20morning%20radio%20program%20cover%20art%2C%20warm%20sunrise%20tones%2C%20dove%20and%20cross%20symbols%2C%20peaceful%20spiritual%20design%2C%20square%20format%2C%20soft%20pastel%20colors%2C%20elegant%20typography&width=200&height=200&seq=sn-notif-prog-4&orientation=squarish",
    events: [
      { eventId: "program_started", enabled: false },
      { eventId: "new_post", enabled: true },
      { eventId: "new_event", enabled: false },
      { eventId: "winner_announced", enabled: false },
    ],
  },
  {
    programId: 5, programName: "Carrusel Deportivo", radioName: "Deportes Total Radio",
    programImage: "https://readdy.ai/api/search-image?query=Sports%20radio%20program%20cover%20art%2C%20soccer%20ball%20and%20stadium%20theme%2C%20dynamic%20blue%20and%20gold%20design%2C%20energetic%20feel%2C%20square%20format%2C%20bold%20sports%20typography&width=200&height=200&seq=sn-notif-prog-5&orientation=squarish",
    events: [
      { eventId: "program_started", enabled: true },
      { eventId: "new_post", enabled: true },
      { eventId: "new_event", enabled: true },
      { eventId: "winner_announced", enabled: false },
    ],
  },
];

// --- CONFIGURACIÓN POR LOCUTOR ---

export interface HostNotifConfig {
  hostId: number;
  hostName: string;
  hostImage: string;
  radioName: string;
  events: { eventId: string; enabled: boolean }[];
}

export const hostNotifConfigs: HostNotifConfig[] = [
  {
    hostId: 1, hostName: "Valentina Ríos", radioName: "La Mexicana FM",
    hostImage: "https://readdy.ai/api/search-image?query=Portrait%20of%20a%20warm%20smiling%20Latina%20radio%20host%20woman%2C%20professional%20studio%20headshot%2C%20friendly%20expression%2C%20soft%20lighting%2C%20clean%20background&width=200&height=200&seq=sn-notif-host-1&orientation=squarish",
    events: [
      { eventId: "host_started", enabled: true },
      { eventId: "new_post", enabled: true },
      { eventId: "new_event", enabled: false },
    ],
  },
  {
    hostId: 2, hostName: "Roberto Aguilar", radioName: "Radio Nueva Actitud",
    hostImage: "https://readdy.ai/api/search-image?query=Portrait%20of%20a%20charismatic%20Latino%20male%20radio%20host%2C%20professional%20studio%20headshot%2C%20confident%20smile%2C%20warm%20lighting%2C%20clean%20background&width=200&height=200&seq=sn-notif-host-2&orientation=squarish",
    events: [
      { eventId: "host_started", enabled: true },
      { eventId: "new_post", enabled: false },
      { eventId: "new_event", enabled: true },
    ],
  },
  {
    hostId: 3, hostName: "Andrés Castillo", radioName: "Rock Total Radio",
    hostImage: "https://readdy.ai/api/search-image?query=Portrait%20of%20a%20handsome%20Latino%20male%20radio%20broadcaster%2C%20stylish%20professional%20headshot%2C%20friendly%20expression%2C%20studio%20lighting%2C%20clean%20background&width=200&height=200&seq=sn-notif-host-3&orientation=squarish",
    events: [
      { eventId: "host_started", enabled: false },
      { eventId: "new_post", enabled: true },
      { eventId: "new_event", enabled: true },
    ],
  },
  {
    hostId: 4, hostName: "Camila Ortega", radioName: "Banda Radio MX",
    hostImage: "https://readdy.ai/api/search-image?query=Portrait%20of%20a%20beautiful%20Latina%20female%20radio%20host%2C%20elegant%20professional%20headshot%2C%20bright%20expression%2C%20soft%20studio%20lighting%2C%20clean%20background&width=200&height=200&seq=sn-notif-host-4&orientation=squarish",
    events: [
      { eventId: "host_started", enabled: true },
      { eventId: "new_post", enabled: true },
      { eventId: "new_event", enabled: false },
    ],
  },
  {
    hostId: 5, hostName: "Javier Solís", radioName: "Deportes Total Radio",
    hostImage: "https://readdy.ai/api/search-image?query=Portrait%20of%20a%20serious%20Latino%20male%20radio%20sports%20commentator%2C%20professional%20headshot%2C%20intense%20expression%2C%20dramatic%20studio%20lighting%2C%20clean%20background&width=200&height=200&seq=sn-notif-host-5&orientation=squarish",
    events: [
      { eventId: "host_started", enabled: true },
      { eventId: "new_post", enabled: true },
      { eventId: "new_event", enabled: true },
    ],
  },
];

// --- CONFIGURACIÓN POR CLUB (NUEVO) ---

export interface ClubNotifConfig {
  clubId: number;
  clubName: string;
  clubImage: string;
  radioName: string;
  members: number;
  events: { eventId: string; enabled: boolean }[];
}

export const clubNotifConfigs: ClubNotifConfig[] = [
  {
    clubId: 1, clubName: "Club Oficial La Mexicana", radioName: "La Mexicana FM", members: 3842,
    clubImage: "https://readdy.ai/api/search-image?query=Mexican%20fan%20club%20badge%20design%2C%20circular%20emblem%20with%20warm%20orange%20and%20green%20colors%2C%20community%20spirit%2C%20clean%20modern%20style%2C%20golden%20accents%2C%20flat%20vector%20design&width=200&height=200&seq=sn-notif-club-1&orientation=squarish",
    events: [
      { eventId: "new_contest", enabled: true },
      { eventId: "new_event", enabled: true },
      { eventId: "winner_announced", enabled: true },
      { eventId: "new_post", enabled: true },
    ],
  },
  {
    clubId: 2, clubName: "Rock Total Fans", radioName: "Rock Total Radio", members: 2931,
    clubImage: "https://readdy.ai/api/search-image?query=Rock%20fan%20club%20badge%20design%2C%20circular%20emblem%20with%20black%20and%20red%20colors%2C%20electric%20guitar%20motif%2C%20bold%20modern%20style%2C%20flat%20vector%20design&width=200&height=200&seq=sn-notif-club-2&orientation=squarish",
    events: [
      { eventId: "new_contest", enabled: false },
      { eventId: "new_event", enabled: true },
      { eventId: "winner_announced", enabled: false },
      { eventId: "new_post", enabled: true },
    ],
  },
  {
    clubId: 3, clubName: "Banda Lovers Club", radioName: "Banda Radio MX", members: 4198,
    clubImage: "https://readdy.ai/api/search-image?query=Banda%20music%20fan%20club%20badge%2C%20circular%20emblem%20with%20red%20white%20and%20green%20colors%2C%20trumpet%20motif%2C%20festive%20Mexican%20style%2C%20flat%20vector%20design&width=200&height=200&seq=sn-notif-club-3&orientation=squarish",
    events: [
      { eventId: "new_contest", enabled: true },
      { eventId: "new_event", enabled: true },
      { eventId: "winner_announced", enabled: true },
      { eventId: "new_post", enabled: false },
    ],
  },
  {
    clubId: 4, clubName: "Club Fe y Esperanza", radioName: "Radio Fe y Esperanza", members: 2156,
    clubImage: "https://readdy.ai/api/search-image?query=Christian%20community%20club%20badge%20design%2C%20circular%20emblem%20with%20dove%20symbol%2C%20soft%20warm%20colors%2C%20spiritual%20theme%2C%20flat%20vector%20design&width=200&height=200&seq=sn-notif-club-4&orientation=squarish",
    events: [
      { eventId: "new_contest", enabled: false },
      { eventId: "new_event", enabled: true },
      { eventId: "winner_announced", enabled: false },
      { eventId: "new_post", enabled: true },
    ],
  },
  {
    clubId: 5, clubName: "Deportes Total Club", radioName: "Deportes Total Radio", members: 5087,
    clubImage: "https://readdy.ai/api/search-image?query=Sports%20fan%20club%20badge%20design%2C%20circular%20emblem%20with%20blue%20and%20gold%20colors%2C%20trophy%20motif%2C%20dynamic%20modern%20style%2C%20flat%20vector%20design&width=200&height=200&seq=sn-notif-club-5&orientation=squarish",
    events: [
      { eventId: "new_contest", enabled: true },
      { eventId: "new_event", enabled: true },
      { eventId: "winner_announced", enabled: true },
      { eventId: "new_post", enabled: true },
    ],
  },
];

// --- PLAN LIMITS ---

export interface PlanNotifLimit {
  planId: string;
  planName: string;
  notifLimit: number;
  notifLimitDisplay: string;
  price: string;
  popular: boolean;
  color: string;
  bgColor: string;
  borderColor: string;
}

export const planNotifLimits: PlanNotifLimit[] = [
  {
    planId: "free", planName: "Free", notifLimit: 500, notifLimitDisplay: "500/mes",
    price: "Gratis", popular: false,
    color: "text-foreground-700", bgColor: "bg-background-50", borderColor: "border-background-200",
  },
  {
    planId: "basico", planName: "Básico", notifLimit: 2500, notifLimitDisplay: "2,500/mes",
    price: "$9.99/mes", popular: false,
    color: "text-foreground-700", bgColor: "bg-background-50", borderColor: "border-background-200",
  },
  {
    planId: "pro", planName: "Pro", notifLimit: 10000, notifLimitDisplay: "10,000/mes",
    price: "$29.99/mes", popular: true,
    color: "text-primary-700", bgColor: "bg-primary-50", borderColor: "border-primary-300",
  },
  {
    planId: "premium", planName: "Premium", notifLimit: 50000, notifLimitDisplay: "50,000/mes",
    price: "$59.99/mes", popular: false,
    color: "text-foreground-700", bgColor: "bg-background-50", borderColor: "border-background-200",
  },
  {
    planId: "ilimitado", planName: "Ilimitado", notifLimit: -1, notifLimitDisplay: "Sin límite",
    price: "$99.99/mes", popular: false,
    color: "text-foreground-700", bgColor: "bg-background-50", borderColor: "border-background-200",
  },
];

// --- HELPERS ---

export function getEventTypeById(id: string): NotificationEventType | undefined {
  return notificationEventTypes.find((e) => e.id === id);
}

export function getMetricsForEvent(eventId: string): EventTypeMetrics | undefined {
  return eventTypeMetrics.find((m) => m.eventId === eventId);
}

export function getTotalEnabledEvents(configs: { events: { eventId: string; enabled: boolean }[] }[]): number {
  return configs.reduce((sum, c) => sum + c.events.filter((e) => e.enabled).length, 0);
}

export function getGlobalEnabledCount(): number {
  return getTotalEnabledEvents(radioNotifConfigs) +
    getTotalEnabledEvents(programNotifConfigs) +
    getTotalEnabledEvents(hostNotifConfigs) +
    getTotalEnabledEvents(clubNotifConfigs);
}

export function formatMetricValue(value: number, decimals: number = 1): string {
  if (value >= 1000000) return (value / 1000000).toFixed(decimals) + "M";
  if (value >= 1000) return (value / 1000).toFixed(decimals) + "K";
  return value.toFixed(decimals);
}